import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Crown, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [premiumType, setPremiumType] = useState(null);

  useEffect(() => {
    // PayPal returns paymentId and PayerID
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    
    if (paymentId && payerId) {
      executePayPalPayment(paymentId, payerId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const executePayPalPayment = async (paymentId, payerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payment/execute`, {
        params: { paymentId, PayerID: payerId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatus('success');
        setPremiumType(response.data.premium_type);
        
        // CRITICAL: Refresh user data to update is_premium status
        await refreshUser();
        
        toast.success('🎉 Paiement réussi !');
      } else {
        setStatus('error');
        toast.error('Erreur lors du paiement');
      }
    } catch (error) {
      console.error('Error executing payment:', error);
      setStatus('error');
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification du paiement');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center"
      >
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
            <h1 className="font-playfair text-2xl font-bold text-charcoal mb-2">
              Vérification du paiement...
            </h1>
            <p className="font-nunito text-charcoal/60">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', bounce: 0.5 }}
              className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            <h1 className="font-playfair text-3xl font-bold text-charcoal mb-2">
              Paiement Réussi !
            </h1>
            <p className="font-nunito text-charcoal/60 mb-6">
              Bienvenue dans MoroccoTalk Premium ! 🇲🇦
            </p>
            
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4 mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-6 h-6 text-amber-500" />
                <span className="font-nunito font-bold text-amber-700">
                  {premiumType === 'lifetime' ? 'Accès à Vie Débloqué' : 'Premium Mensuel Activé'}
                </span>
              </div>
              <p className="font-nunito text-sm text-amber-600">
                Tout le contenu est maintenant disponible !
              </p>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-nunito font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-opacity"
              data-testid="go-to-dashboard-btn"
            >
              Commencer à Apprendre
              <ArrowRight className="w-5 h-5" />
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">😕</span>
            </div>
            <h1 className="font-playfair text-2xl font-bold text-charcoal mb-2">
              Une erreur s'est produite
            </h1>
            <p className="font-nunito text-charcoal/60 mb-6">
              Nous n'avons pas pu vérifier votre paiement. Si vous avez été débité, veuillez nous contacter sur WhatsApp.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/premium')}
                className="w-full bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-4 rounded-xl transition-colors"
              >
                Réessayer
              </button>
              <button
                onClick={() => window.open('https://wa.me/212669490473', '_blank')}
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-nunito font-bold py-4 rounded-xl transition-colors"
              >
                Contacter sur WhatsApp
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
